import { Component, effect, EventEmitter, inject, input, Input, model, output, Output, signal, untracked } from "@angular/core";
import { Router } from "@angular/router";
import { DocumentPickerService } from "./document-picker.service";
import { DocumentMetadataDto } from "./DocumentMetadataDto";
import { MatCardModule } from "@angular/material/card";
import { MatDividerModule } from "@angular/material/divider";
import { MatInputModule } from "@angular/material/input";
import { FormsModule } from "@angular/forms";
import { MatListModule } from "@angular/material/list";
import { MatButtonModule } from "@angular/material/button";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { FileDragAndDropDirective } from "../../directives/file-drag-and-drop.directive";
import { ScrollInsetShadowDirective } from "../../directives/scroll-inset-shadow.directive";

interface FileItem {
  File: File;
  Filename: string;
  Status: string;
  IsUploading: boolean;
  IsError: boolean;
  CanDownload: boolean;
  Metadata: DocumentMetadataDto;
  DownloadLink?: string;
}

@Component({
  selector: 'document-picker',
  templateUrl: 'document-picker.component.html',
  styleUrls: ["document-picker.component.css"],
  standalone: true,
  imports: [CommonModule, FormsModule, MatCardModule, MatDividerModule, MatInputModule,
    MatListModule, MatButtonModule, MatIconModule, FileDragAndDropDirective, ScrollInsetShadowDirective],
  providers: [DocumentPickerService]
})

export class DocumentPickerComponent {
  private documentPickerService = inject(DocumentPickerService);
  private router = inject(Router)

  protected Files = signal<FileItem[]>([]);
  protected inputValue;

  public canAdd = input<boolean>(true);
  public canRemove = input<boolean>(true);
  public isAtLeastOneRequired = input<boolean>(false);
  public documents = model<DocumentMetadataDto[]>([]);

  constructor() {
    effect(() => {

      const currentDocuments = this.documents();
      if (currentDocuments == null) {
        this.Files.set([]);
        return;
      }

      const documentIds = new Set(currentDocuments.map((d) => d.Id));
      const currentFiles = untracked(() => this.Files());

      // Keep any local-only entries (e.g. uploading) and any documents still present.
      let nextFiles = currentFiles.filter((f) => f.Metadata == null || documentIds.has(f.Metadata.Id));

      const existingDocumentIds = new Set(nextFiles
          .filter((f) => f.Metadata != null)
          .map((f) => f.Metadata.Id));

      const newUploadedFiles: FileItem[] = currentDocuments
        .filter((d) => !existingDocumentIds.has(d.Id))
        .map((d) => ({
          CanDownload: true,
          File: null,
          Filename: d.Filename,
          IsError: false,
          IsUploading: false,
          Metadata: d,
          Status: "Uploaded",
          DownloadLink: this.GenerateDownloadLink(d)
        }));

      if (newUploadedFiles.length > 0) {
        nextFiles = [...nextFiles, ...newUploadedFiles];
      }

      // Always set so the signal notifies dependents even if array reference didn't change.
      this.Files.set(nextFiles);
    }, { allowSignalWrites: true });
  }

  public documentsChange = output<DocumentMetadataDto[]>();
  public safeToSaveParent = output<boolean>();

  private doesFilesListContainMetadata = (metadata: DocumentMetadataDto) =>
    this.Files().findIndex((f) => f.Metadata != null && f.Metadata.Id === metadata.Id) >= 0;

  protected DisableDeleteButton() {
    if (this.canRemove() == false || this.isAtLeastOneRequired() && this.Files().length <= 1)
      return true;

    return false;
  }

  protected droppedFiles(fileList: FileList) {
    if (this.canAdd() == false)
      return;

    this.addFiles(fileList);
  }

  protected browsedFiles(evt: Event) {
    const fileList: FileList = (evt.target as HTMLInputElement).files;
    this.addFiles(fileList);
  }

  protected removeFile(file: FileItem) {
    const index = this.Files().indexOf(file);
    if (index >= 0) {
      this.Files.update((current) => current.filter((f) => f !== file));

      if (file.Metadata != null) {
        const documentIdToRemove = file.Metadata.Id;

        const metadataIndex = this.documents().findIndex((d) => d.Id === documentIdToRemove);
        if (metadataIndex >= 0) {
          this.documents.update((current) => current.filter((d) => d.Id !== documentIdToRemove));
          this.documentsChange.emit(this.documents());
        }
      }
    }
  }

  protected backgroundColor(file: FileItem) {
    if (file.IsError)
      return 'bg-danger';
    else if (file.IsUploading)
      return 'bg-info';
    else
      return '';
  }

  private addFiles(fileList: FileList) {
    this.safeToSaveParent.emit(false);

    if (fileList != null) {
      for (let i = 0; i < fileList.length; i++) {
        const file: File = fileList[i];

        // Don't include files with the same filename
        if (this.Files().some(f => f.Filename === file.name))
          continue;

        const fileToAdd: FileItem = {
          File: file,
          Filename: file.name,
          Status: "Uploading",
          IsUploading: true,
          IsError: false,
          CanDownload: false,
          Metadata: null
        }

        this.Files.update((current) => [...current, fileToAdd]);

        this.documentPickerService.UploadFile(file)
          .subscribe({
            next: (fileResult) => {
              this.Files.update((current) =>
                current.map((f) => {
                  if (f !== fileToAdd)
                    return f;

                  return {
                    ...f,
                    IsUploading: false,
                    Status: "Uploaded",
                    Metadata: fileResult,
                    CanDownload: true,
                    DownloadLink: this.GenerateDownloadLink(fileResult)
                  };
                })
              );

              this.documents.update((current) => [...current, fileResult]);
              this.documentsChange.emit(this.documents());
              this.safeToSaveParent.emit(true);
            }, 
            error: (err) => {
              this.Files.update((current) =>
                current.map((f) => {
                  if (f !== fileToAdd)
                    return f;

                  return {
                    ...f,
                    IsUploading: false,
                    Status: "Error uploading",
                    IsError: true
                  };
                })
              );
              console.log(err);
            }
          });
      }
    }

    // clear input value to always trigger change event
    // removing and attempting to re-add same file via file browser bypasses change event
    this.inputValue = undefined;
  }

  private GenerateDownloadLink = (file: DocumentMetadataDto): string => {
    const urlTree = this.router.createUrlTree([`api/v1/documents/${file.Id}/download`], {});

    return window.location.origin + urlTree.toString();
  }

  protected GetOverallStatus() {
    const uploadCount = this.Files().filter((a) => a.IsUploading).length;
    const errorCount = this.Files().filter((a) => a.IsError).length;
    const totalCount = this.Files().length;

    let msg: string = "";
    if (uploadCount > 0)
      msg += `Uploading ${uploadCount} files.`

    if (errorCount > 0)
      msg += `${errorCount} errors`;

    if (uploadCount == 0 && errorCount == 0)
      msg = `${totalCount} files`;

    return msg;
  }
}
